import "./versionCheck";
import "./index.css";
import React, { useEffect, useState, Suspense } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import AutoInvoiceShareHost from "./components/AutoInvoiceShareHost";
import { PLAN_LEVELS } from "./constants/planLevels";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import RegistrationForm from "./pages/registration";
import Dashboard, { DashboardForUser } from "./pages/dashboard";
import store from "./store/store";
import "./i18n";
import HelpAndSupportPage from "./components/help&support";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { ThemeProvider } from "./constants/there";
import NotFound from "./pages/notFound";
import { GlobalModalsProvider } from "./components/shortcutContext";
import GlobalShortcuts from "./components/shortcutComponent";
import ModalRenderer from "./components/modalrander";
import { NavigationProvider } from "./components/navigation";
import Layout from "./components/sidebar/layout";
import OnlineStoreOrdersPage from "./pages/online-store/online-store";
import ThemeGalleryPage from "./pages/online-store/ThemeGalleryPage";
import ThemePreviewPage from "./pages/online-store/ThemePreviewPage";
import CustomThemeBuilderPage from "./pages/online-store/CustomThemeBuilderPage";
import StoreFrontPage from "./pages/online-store/storeFrontPage";
import PrivateRoute from "./components/PrivateRoute";
import ProtectedRoute from "./components/common/ProtectedRoute";
import OnlineServiceOrdersPage from "./pages/online-store/online-services";
import OnlineServiceBookingDashboardPage from "./pages/online-store/OnlineServiceBookingDashboardPage";
import ServiceBookingPage from "./pages/online-store/ServiceBookingPage";
import InvoicePage from "./pages/online-store/InvoicePage";
import ServiceInvoicePage from "./pages/online-store/ServiceInvoicePage";
import OrderHistoryPage from "./pages/online-store/OrderHistoryPage";
import ProfileDetailsPage from "./pages/online-store/ProfileDetailsPage";
import CartPage from "./pages/online-store/CartModel";
// import ShippingManagementPage from "./pages/online-store/ShippingManagementPage";
import LandingPage from "./pages/online-store/LandingPage";
import ProductPage from "./pages/online-store/ProductDetailPage";
// Import customer context and components
import { ResellerCustomerProvider } from "./contexts/ResellerCustomerContext";
import CustomerProtectedRoute from "./components/CustomerProtectedRoute";

// Import the LogoLoading component
import { LogoLoading } from "./components/common/utlity/utility";
import { syncTracking } from "./components/common/utlity/tracking";
import ShippingManagementPage from "./pages/online-store/ShippingManagementPage";
import SubdomainChecker from "./components/subdomainChecker";
import RiskManagementPage from "./pages/online-store/RiskManagementPage";
import GlobalServiceReviewPrompt from "./pages/online-store/GlobalServiceReviewPrompt";
import SellerInbox from "./components/SellerInbox";
import {
  BusinessFeatureRoute,
  FeatureSettingRoute,
} from "./components/sidebar/BusinessSettingsContext";
// Reseller Store Pages (lazy loaded)
const ResellerStorefront = React.lazy(
  () => import("./pages/store/ResellerStorefront"),
);
const ResellerCart = React.lazy(() => import("./pages/store/ResellerCart"));
const ResellerCheckout = React.lazy(
  () => import("./pages/store/ResellerCheckout"),
);
const ResellerOrderConfirmation = React.lazy(
  () => import("./pages/store/ResellerOrderConfirmation"),
);
const ResellerCustomerLogin = React.lazy(
  () => import("./pages/store/ResellerCustomerLogin"),
);
const ResellerCustomerRegister = React.lazy(
  () => import("./pages/store/ResellerCustomerRegister"),
);
const ResellerCustomerOrders = React.lazy(
  () => import("./pages/store/ResellerCustomerOrders"),
);
const ResellerCustomerOrderDetails = React.lazy(
  () => import("./pages/store/ResellerCustomerOrderDetails"),
);
const ResellerCustomerProfile = React.lazy(
  () => import("./pages/store/ResellerCustomerProfile"),
);
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const WishlistPage = React.lazy(
  () => import("./pages/online-store/storeSetting/WishlistPage"),
);
const LanguageSelectionPage = React.lazy(
  () => import("./components/LanguageSelectionPage"),
);
const SubscriptionPlans = React.lazy(() => import("./pages/SubscriptionPlans"));

const AppWithProviders = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const handleUnload = () => {
      syncTracking("unload");
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // visibilitychange
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        syncTracking("unload");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      syncTracking("interval");
    }, 100000);
    return () => clearInterval(interval);
  }, []);
  if (loading) {
    return <LogoLoading />;
  }

  return (
    <NavigationProvider navigate={navigate}>
      <GlobalShortcuts />
      <ModalRenderer />
      <AutoInvoiceShareHost />
      <GlobalServiceReviewPrompt />
      {/* Wrap routes in Suspense for lazy loading components if needed */}
      <Suspense fallback={<LogoLoading />}>
        <Routes>
          <Route index path="" element={<App />} />
          <Route path="/register" element={<RegistrationForm />} />

          {/* Add :businessName parameter to all business routes */}
          <Route
            path="/:businessName/:bussinessUuid/online-store"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <OnlineStoreOrdersPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-store/themes"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <ThemeGalleryPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-store/themes/preview/:themeId"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <ThemePreviewPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-store/themes/builder"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                  <CustomThemeBuilderPage />
                </BusinessFeatureRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-store/risk-management"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <RiskManagementPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/invoice/:orderId"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <InvoicePage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/service-invoice/:bookingId"
            element={
              <BusinessFeatureRoute setting="onlineServices" disabledMessage="Online services are turned off for this business.">
                <ServiceInvoicePage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/orders"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <OrderHistoryPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/profile"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <ProfileDetailsPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/cart"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <CartPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-services"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineServices">
                    <OnlineServiceOrdersPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <StoreFrontPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/product/:productId"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <ProductPage />
              </BusinessFeatureRoute>
            }
          />
          <Route path="/:businessName/p/:shortCode" element={<ProductPage />} />
          <Route
            path="/:bussinessUuid/dashboard/*"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/landingPage"
            element={<LandingPage />}
          />

          {/* Keep old routes for backward compatibility */}
          <Route
            path="/:bussinessUuid/online-store"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <OnlineStoreOrdersPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:bussinessUuid/online-store/themes"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <ThemeGalleryPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:bussinessUuid/online-store/themes/preview/:themeId"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <ThemePreviewPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:bussinessUuid/online-store/themes/builder"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                  <CustomThemeBuilderPage />
                </BusinessFeatureRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:bussinessUuid/online-store/risk-management"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <RiskManagementPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:bussinessUuid/online-store/order-details/:orderId"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineStore">
                    <ShippingManagementPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/:bussinessUuid/invoice/:orderId"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <InvoicePage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/store/:bussinessUuid/orders"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <OrderHistoryPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/:bussinessUuid/online-services/booking-dashboard"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineServices">
                    <OnlineServiceBookingDashboardPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/booking/:serviceId"
            element={
              <BusinessFeatureRoute setting="onlineServices" disabledMessage="Online services are turned off for this business.">
                <ServiceBookingPage />
              </BusinessFeatureRoute>
            }
          />

          <Route
            path="/:bussinessUuid/online-services/booking-dashboard"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineServices">
                    <OnlineServiceBookingDashboardPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="store/:bussinessUuid/booking/:serviceId"
            element={
              <BusinessFeatureRoute setting="onlineServices" disabledMessage="Online services are turned off for this business.">
                <ServiceBookingPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/store/:bussinessUuid/profile"
            element={<ProfileDetailsPage />}
          />
          <Route
            path="/:businessName/store/:bussinessUuid/select-language"
            element={<LanguageSelectionPage />}
          />
          <Route
            path="/store/:bussinessUuid/select-language"
            element={<LanguageSelectionPage />}
          />
          <Route
            path="/store/:bussinessUuid/cart"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <CartPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:businessName/store/:bussinessUuid/wishlist"
            element={<WishlistPage />}
          />

          <Route
            path="/:bussinessUuid/online-services"
            element={
              <ProtectedRoute requiredLevel={PLAN_LEVELS.GROWTH}>
                <Layout>
                  <FeatureSettingRoute setting="onlineServices">
                    <OnlineServiceOrdersPage />
                  </FeatureSettingRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="store/:bussinessUuid/"
            element={
              <BusinessFeatureRoute setting="onlineStore" disabledMessage="The online store is turned off for this business.">
                <StoreFrontPage />
              </BusinessFeatureRoute>
            }
          />
          <Route
            path="/:bussinessUuid/dashboard/*"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route path="/:bussinessUuid/landingPage" element={<LandingPage />} />

          {/* Reseller Store Public Routes */}
          <Route
            path="/online-store/:resellerUuid"
            element={
              <ResellerCustomerProvider>
                <ResellerStorefront />
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/cart"
            element={
              <ResellerCustomerProvider>
                <ResellerCart />
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/checkout"
            element={
              <ResellerCustomerProvider>
                <ResellerCheckout />
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/order-confirmation/:orderId"
            element={
              <ResellerCustomerProvider>
                <ResellerOrderConfirmation />
              </ResellerCustomerProvider>
            }
          />

          {/* Customer Authentication Routes */}
          <Route
            path="/online-store/:resellerUuid/login"
            element={
              <ResellerCustomerProvider>
                <ResellerCustomerLogin />
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/register"
            element={
              <ResellerCustomerProvider>
                <ResellerCustomerRegister />
              </ResellerCustomerProvider>
            }
          />

          {/* Customer Protected Routes */}
          <Route
            path="/online-store/:resellerUuid/profile"
            element={
              <ResellerCustomerProvider>
                <CustomerProtectedRoute resellerUuid="">
                  <ResellerCustomerProfile />
                </CustomerProtectedRoute>
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/orders"
            element={
              <ResellerCustomerProvider>
                <CustomerProtectedRoute resellerUuid="">
                  <ResellerCustomerOrders />
                </CustomerProtectedRoute>
              </ResellerCustomerProvider>
            }
          />
          <Route
            path="/online-store/:resellerUuid/orders/:orderId"
            element={
              <ResellerCustomerProvider>
                <CustomerProtectedRoute resellerUuid="">
                  <ResellerCustomerOrderDetails />
                </CustomerProtectedRoute>
              </ResellerCustomerProvider>
            }
          />

          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/user-ff/*" element={<DashboardForUser />} />
          <Route path="/help-support" element={<HelpAndSupportPage />} />
          <Route path="/pricing" element={<SubscriptionPlans />} />
          <Route
            path="/inbox"
            element={
              <Layout>
                <SellerInbox />
              </Layout>
            }
          />
          <Route
            path="/:bussinessUuid/inbox"
            element={
              <Layout>
                <SellerInbox />
              </Layout>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <ToastContainer position="top-right" autoClose={2500} />
    </NavigationProvider>
  );
};

const RootComponent = () => {
  const [themeMode, setThemeMode] = useState(
    localStorage.getItem("themeMode") || "light",
  );
  const [appLoading, setAppLoading] = useState(true);

  const lightTheme = () => {
    setThemeMode("light");
    localStorage.setItem("themeMode", "light");
  };

  const darkTheme = () => {
    setThemeMode("dark");
    localStorage.setItem("themeMode", "dark");
  };

  useEffect(() => {
    // Initialize theme
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(themeMode);

    // Simulate app initialization
    const timer = setTimeout(() => {
      setAppLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [themeMode]);

  // Show loading at the root level while theme and app initializes
  if (appLoading) {
    return <LogoLoading />;
  }

  return (
    <ThemeProvider value={{ themeMode, lightTheme, darkTheme }}>
      <Provider store={store}>
        <Router>
          <GlobalModalsProvider>
            <SubdomainChecker>
              <AppWithProviders />
            </SubdomainChecker>
          </GlobalModalsProvider>
        </Router>
      </Provider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));

// Cashfree loads the return_url inside its checkout iframe. Don't boot the
// whole app there (it would run a second copy of the app/auth); just tell the
// parent window that checkout finished.
// Also covers the case where Cashfree opens checkout in a popup window.
const returnParams = new URLSearchParams(window.location.search);
const isFramed = window.self !== window.top;
const isPopup = !isFramed && Boolean(window.opener);
const isCheckoutReturnFrame =
  (isFramed || isPopup) && returnParams.has("subscription_id");

if (isCheckoutReturnFrame) {
  try {
    const target = isFramed ? window.parent : window.opener;
    target.postMessage(
      {
        type: "CASHFREE_SUBSCRIPTION_RETURN",
        subscriptionId: returnParams.get("subscription_id"),
      },
      window.location.origin,
    );
    if (isPopup) setTimeout(() => window.close(), 300);
  } catch (e) {
    console.error(e);
  }
  root.render(<p style={{ padding: 24, textAlign: "center" }}>Processing payment...</p>);
} else {
  root.render(<RootComponent />);
}

reportWebVitals();
